import React from 'react';
import PieChart from '@cloudscape-design/components/pie-chart';
import BarChart from '@cloudscape-design/components/bar-chart';
import { Box, SpaceBetween, Header, Container } from "@cloudscape-design/components";

const SATISFACTION_COLORS = {
  Positiva: '#2d7d46',
  Neutra: '#e6a817',
  Negativa: '#c62828',
  'Sin dato': '#9e9e9e',
};

const EMOTION_PALETTE = [
  '#7c4dff', '#00b8d4', '#00695c', '#ad1457', '#f4511e',
  '#0277bd', '#558b2f', '#6d4c41', '#546e7a', '#37474f',
];

export default function SatisfactionTab({ satisfactionMetrics, emotionMetrics, t }) {
  if (!satisfactionMetrics) {
    return (
      <Box textAlign="center" color="text-body-secondary" padding={{ vertical: "xxxl" }}>
        {t("insights.detail.noSatisfactionData", {
          defaultValue: "No hay datos de satisfacción disponibles para este resumen."
        })}
      </Box>
    );
  }

  // Datos para PieChart de Cloudscape (omitir segmentos en 0)
  const pieData = Object.entries(satisfactionMetrics.counts || {})
    .filter(([, v]) => v > 0)
    .map(([title, value]) => ({
      title,
      value,
      percentage: satisfactionMetrics.percentages?.[title] ?? 0,
      color: SATISFACTION_COLORS[title],
    }));

  // Datos para BarChart de Cloudscape (top 10 emociones)
  const total = satisfactionMetrics.total || 1;
  const topEmotions = (emotionMetrics?.ranking || []).slice(0, 10);

  const barSeries = topEmotions.map(([emotion, count], i) => ({
    title: emotion.charAt(0).toUpperCase() + emotion.slice(1),
    type: 'bar',
    color: EMOTION_PALETTE[i % EMOTION_PALETTE.length],
    data: [{ x: emotion.charAt(0).toUpperCase() + emotion.slice(1), y: Math.round(count / total * 100) }],
  }));

  const barXDomain = topEmotions.map(([emotion]) =>
    emotion.charAt(0).toUpperCase() + emotion.slice(1)
  );

  const i18nStrings = {
    legendAriaLabel: "Leyenda",
    chartAriaRoleDescription: "gráfico",
    xAxisAriaRoleDescription: "eje X",
    yAxisAriaRoleDescription: "eje Y",
    detailPopoverDismissAriaLabel: "Cerrar",
  };

  return (
    <SpaceBetween size="l">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Gráfico de torta — satisfacción */}
        <Container header={<Header variant="h3">Satisfacción de los clientes</Header>}>
          <PieChart
            data={pieData}
            height={300}
            detailPopoverContent={(datum) => [
              { key: "Cantidad", value: datum.value },
              { key: "Porcentaje", value: `${datum.percentage}%` },
            ]}
            segmentDescription={(datum) => `${datum.value} units, ${datum.percentage}%`}
            ariaLabel="Gráfico de satisfacción"
            ariaDescription="Distribución de satisfacción de clientes"
            hideFilter
            i18nStrings={{
              ...i18nStrings,
              detailsValue: "Valor",
              detailsPercentage: "Porcentaje",
              filterLabel: "Filtrar segmentos",
              filterPlaceholder: "Filtrar segmentos",
              filterSelectedAriaLabel: "seleccionado",
              legendAriaLabel: "Leyenda",
              chartAriaRoleDescription: "gráfico de torta",
            }}
            empty={
              <Box textAlign="center" color="text-body-secondary">
                Sin datos de satisfacción
              </Box>
            }
          />
        </Container>

        {/* Gráfico de barras — emociones */}
        <Container header={<Header variant="h3">Emociones más frecuentes</Header>}>
          {topEmotions.length > 0 ? (
            <BarChart
              series={barSeries}
              height={300}
              xScaleType="categorical"
              yScaleType="linear"
              xDomain={barXDomain}
              yDomain={[0, 100]}
              xTitle="Emociones"
              yTitle="Porcentaje"
              hideFilter
              stackedBars={false}
              ariaLabel="Gráfico de emociones"
              ariaDescription="Emociones más frecuentes detectadas en las llamadas"
              i18nStrings={{
                ...i18nStrings,
                chartAriaRoleDescription: "gráfico de barras",
                yTickFormatter: (v) => `${v}%`,
              }}
              empty={
                <Box textAlign="center" color="text-body-secondary">
                  Sin datos de emociones
                </Box>
              }
            />
          ) : (
            <Box textAlign="center" color="text-body-secondary" padding={{ vertical: "l" }}>
              No se detectaron emociones en las llamadas analizadas.
            </Box>
          )}
        </Container>
      </div>

      <Box color="text-body-secondary" variant="p">
        Total de interacciones analizadas: <strong>{satisfactionMetrics.total}</strong>
      </Box>
    </SpaceBetween>
  );
}
